import { useState } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { UploadSimple, Download, FileText, Warning, Check, X } from '@phosphor-icons/react';

export default function ImportHub() {
  return (
    <div className="space-y-5" data-testid="import-hub">
      <h2 className="text-xl font-semibold text-slate-900 font-['Chivo']">Import Data</h2>
      <Tabs defaultValue="donations-import">
        <TabsList className="flex border-b border-slate-200 bg-transparent p-0 h-auto">
          {['Donations Import', 'Bulk Balance Update'].map(tab => (
            <TabsTrigger key={tab} value={tab.toLowerCase().replace(/\s+/g, '-')}
              className="px-4 py-2 text-sm font-medium text-slate-500 border-b-2 border-transparent rounded-none data-[state=active]:text-[#0F6CBD] data-[state=active]:border-[#0F6CBD] data-[state=active]:bg-transparent data-[state=active]:shadow-none"
              data-testid={`import-tab-${tab.toLowerCase().replace(/\s+/g, '-')}`}>{tab}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="donations-import" className="mt-4"><DonationsImport /></TabsContent>
        <TabsContent value="bulk-balance-update" className="mt-4"><BulkBalanceUpdate /></TabsContent>
      </Tabs>
    </div>
  );
}

function DonationsImport() {
  const [file, setFile] = useState(null);
  const [results, setResults] = useState(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async () => {
    if (!file) { toast.error('Select a CSV file'); return; }
    setUploading(true);
    setResults(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post('/import/donations-csv', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResults(data);
      if (data.imported > 0) toast.success(`${data.imported} donations imported`);
      if (data.errors?.length > 0) toast.warning(`${data.errors.length} errors encountered`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setUploading(false);
  };

  const downloadTemplate = () => {
    const csv = 'donor_name,recipient_name,amount,date,reference,notes\nJohn Smith,Jane Doe Student,100.00,2024-01-15,Check #123,Monthly sponsorship\nAcme Corp,,500.00,2024-01-20,Wire-001,Central fund donation';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'donex_donations_template.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4" data-testid="donations-import">
      <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-medium text-slate-800 font-['Chivo']">Import Donations from CSV</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-sm p-4 text-sm text-blue-800">
            <p className="font-medium mb-2">CSV Format Requirements:</p>
            <ul className="list-disc pl-5 space-y-1 text-xs text-blue-700">
              <li><strong>donor_name</strong> — Must match an existing donor exactly</li>
              <li><strong>recipient_name</strong> — Must match an existing recipient (leave empty for Central Fund)</li>
              <li><strong>amount</strong> — Dollar amount (e.g. 100.00)</li>
              <li><strong>date</strong> — YYYY-MM-DD format (e.g. 2024-01-15)</li>
              <li><strong>reference</strong> — Optional reference/receipt number</li>
              <li><strong>notes</strong> — Optional notes</li>
            </ul>
          </div>

          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={downloadTemplate} className="text-xs rounded-sm" data-testid="download-template-btn">
              <Download size={14} className="mr-1" /> Download Template
            </Button>
          </div>

          <div className="flex items-end gap-3">
            <div className="flex-1">
              <Label className="data-label">Select CSV File</Label>
              <Input type="file" accept=".csv" onChange={e => setFile(e.target.files[0])} className="rounded-sm" data-testid="import-file-input" />
            </div>
            <Button onClick={handleUpload} disabled={uploading || !file} className="bg-[#0F6CBD] hover:bg-[#005A9E] text-white rounded-sm" data-testid="import-upload-btn">
              <UploadSimple size={14} className="mr-1" /> {uploading ? 'Importing...' : 'Import'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {results && (
        <Card className="bg-white border border-slate-200 rounded-sm shadow-none" data-testid="import-results">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium text-slate-800 font-['Chivo']">Import Results</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-6 mb-4">
              <div className="flex items-center gap-2">
                <Check size={16} className="text-emerald-600" />
                <span className="text-sm"><span className="font-semibold text-emerald-700">{results.imported}</span> imported</span>
              </div>
              {results.errors?.length > 0 && (
                <div className="flex items-center gap-2">
                  <X size={16} className="text-red-600" />
                  <span className="text-sm"><span className="font-semibold text-red-700">{results.errors.length}</span> errors</span>
                </div>
              )}
            </div>
            {results.errors?.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-sm p-3 max-h-48 overflow-y-auto">
                {results.errors.map((err, i) => (
                  <p key={i} className="text-xs text-red-700 py-0.5">{err}</p>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function BulkBalanceUpdate() {
  const [file, setFile] = useState(null);
  const [results, setResults] = useState(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async () => {
    if (!file) { toast.error('Select a CSV file'); return; }
    setUploading(true);
    setResults(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post('/import/bulk-balance-csv', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResults(data);
      if (data.updated > 0) toast.success(`${data.updated} balances updated`);
      if (data.errors?.length > 0) toast.warning(`${data.errors.length} errors`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setUploading(false);
  };

  const downloadTemplate = () => {
    const csv = 'recipient_name,new_balance\nJane Doe,1500.00\nJohn Student,2000.00';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'donex_balance_template.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4" data-testid="bulk-balance-update">
      <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-medium text-slate-800 font-['Chivo']">Bulk Balance Update</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 text-sm text-amber-800">
            <div className="flex items-start gap-2">
              <Warning size={16} className="mt-0.5 shrink-0" />
              <div>
                <p className="font-medium">Warning: This will overwrite current balances</p>
                <p className="text-xs mt-1 text-amber-700">The new_balance column sets the ABSOLUTE balance (not a delta). Use exact recipient names.</p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={downloadTemplate} className="text-xs rounded-sm" data-testid="download-balance-template-btn">
              <Download size={14} className="mr-1" /> Download Template
            </Button>
          </div>

          <div className="flex items-end gap-3">
            <div className="flex-1">
              <Label className="data-label">Select CSV File</Label>
              <Input type="file" accept=".csv" onChange={e => setFile(e.target.files[0])} className="rounded-sm" data-testid="balance-file-input" />
            </div>
            <Button onClick={handleUpload} disabled={uploading || !file} className="bg-[#D83B01] hover:bg-[#A52C00] text-white rounded-sm" data-testid="balance-upload-btn">
              <UploadSimple size={14} className="mr-1" /> {uploading ? 'Updating...' : 'Update Balances'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {results && (
        <Card className="bg-white border border-slate-200 rounded-sm shadow-none" data-testid="balance-results">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium text-slate-800 font-['Chivo']">Update Results</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-6 mb-4">
              <span className="text-sm"><span className="font-semibold text-emerald-700">{results.updated}</span> updated</span>
              {results.errors?.length > 0 && <span className="text-sm"><span className="font-semibold text-red-700">{results.errors.length}</span> errors</span>}
            </div>
            {results.adjustments?.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-sm overflow-hidden max-h-72 overflow-y-auto">
                <table className="w-full">
                  <thead><tr className="bg-slate-50 border-b border-slate-200">
                    {['Recipient', 'Old Balance', 'New Balance', 'Change'].map(h => <th key={h} className="text-left text-xs font-semibold text-slate-600 uppercase tracking-wider py-2 px-3">{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {results.adjustments.map((a, i) => (
                      <tr key={i} className="border-b border-slate-100">
                        <td className="py-2 px-3 text-sm text-slate-700">{a.name}</td>
                        <td className="py-2 px-3 currency text-sm">${a.old_balance?.toFixed(2)}</td>
                        <td className="py-2 px-3 currency text-sm font-medium">${a.new_balance?.toFixed(2)}</td>
                        <td className={`py-2 px-3 currency text-sm font-medium ${a.change >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {a.change >= 0 ? '+' : ''}{a.change?.toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {results.errors?.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-sm p-3 mt-3 max-h-32 overflow-y-auto">
                {results.errors.map((err, i) => <p key={i} className="text-xs text-red-700 py-0.5">{err}</p>)}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
